import React from 'react'
import { Box, Typography, Button, useMediaQuery, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useImpersonation } from '../../context/impersonationContext'

const BannerContainer = styled(Box)(({ theme }) => ({
  color: 'white',
  display: 'flex',
  minHeight: '60px',
  alignItems: 'center',
  zIndex: 1400,
  backgroundColor: theme.palette.secondary.main,
  width: '100%',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  margin: 0,
  padding: 0,
  
  [theme.breakpoints.up('lg')]: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
  },
  
  [theme.breakpoints.down('lg')]: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  
  '@media print': {
    display: 'none',
  },
}))

const BannerContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  maxWidth: '100%',
  padding: theme.spacing(1.5, 3),
  margin: 0,
  
  [theme.breakpoints.down('md')]: {
    flexDirection: 'row',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
  },
  
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    textAlign: 'center',
    padding: theme.spacing(1),
  },
}))

const BannerText = styled(Box)(({ theme }) => ({
  flex: 1,
  
  [theme.breakpoints.up('md')]: {
    maxWidth: '70%',
  },
  
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
  },
}))

const BannerButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
  
  [theme.breakpoints.up('md')]: {
    maxWidth: '35%',
  },
  
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
}))

const BannerButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: 'white',
  border: '2px solid white',
  fontWeight: 'bold',
  padding: theme.spacing(0.5, 2),
  minWidth: '100px',
  fontSize: '14px',
  
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    border: '2px solid white',
  },
  
  [theme.breakpoints.up('md')]: {
    marginTop: 0,
    maxWidth: '25%',
  },
  
  [theme.breakpoints.down('md')]: {
    marginTop: 0,
    minWidth: '80px',
    padding: theme.spacing(0.5, 1.5),
  },
  
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(0.5),
    width: '100%',
    maxWidth: '150px',
  },
}))

const ImpersonationBanner: React.FC = () => {
  const { isImpersonating, currentUser, originalUser, stopImpersonation } = useImpersonation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))

  if (!isImpersonating || !currentUser || !originalUser) {
    return null
  }

  const handleStopImpersonation = async () => {
    try {
      stopImpersonation()
    } catch (error) {
      console.error('Error stopping impersonation:', error)
    }
  }

  const handleGoToWordPress = () => {
    
    const delegationToken = localStorage.getItem('delegation_auth_token')
    const impersonatedUserId = localStorage.getItem('impersonated_user_id')
    const isImpersonatingFlag = localStorage.getItem('is_impersonating')
    const impersonatedContact = localStorage.getItem('impersonated_contact')
    
    const wordpressUrl = process.env.REACT_APP_ACQ_SITE || 'http://localhost:8082'
    
    if (!delegationToken) {
      console.error('[WordPress Redirect] ERREUR: delegation_auth_token manquant')
      alert('Erreur: Le token de délégation est manquant. Veuillez arrêter la personnification (bouton "Libérer") et re-personnifier l\'utilisateur.')
      return
    }
    
    if (!impersonatedUserId) {
      console.error('[WordPress Redirect] ERREUR: impersonated_user_id manquant')
      alert('Erreur: L\'ID de l\'utilisateur personnifié est manquant. Veuillez arrêter la personnification (bouton "Libérer") et re-personnifier l\'utilisateur.')
      return
    }
    
    const redirectUrl = new URL(`${wordpressUrl}/`)
    redirectUrl.searchParams.set('delegation_token', delegationToken)
    redirectUrl.searchParams.set('impersonated_user_id', impersonatedUserId)
    redirectUrl.searchParams.set('is_impersonating', 'true')
    window.location.href = redirectUrl.toString()
  }

  const impersonateData = currentUser['https://eacq.app.com/impersonateData']
  const impersonatedUserName = impersonateData?.as?.name || currentUser.name || 'Utilisateur'
  
  const bannerText = `Vous avez endossé l'identité de ${impersonatedUserName}`
  const enterpriseText = ''

  return (
    <BannerContainer sx={{ 
      width:'100%' , 
    }}>
      <BannerContent>
        <BannerText>
          <Typography 
            variant="body1" 
            component="div"
            sx={{ 
              fontWeight: '600',
              fontSize: { xs: '14px', sm: '15px', md: '16px' },
              lineHeight: { xs: '18px', sm: '20px', md: '22px' },
              color: 'white',
              wordBreak: 'break-word',
            }}
          >
            {bannerText}{enterpriseText}
          </Typography>
        </BannerText>
        
        <BannerButtons>
          <BannerButton
            variant="outlined"
            onClick={handleGoToWordPress}
            aria-label="Aller sur le site WordPress"
          >
            Site Web
          </BannerButton>
          
          <BannerButton
            variant="outlined"
            onClick={handleStopImpersonation}
            aria-label="Arrêter la personnification"
          >
            Libérer
          </BannerButton>
        </BannerButtons>
      </BannerContent>
    </BannerContainer>
  )
}

export default ImpersonationBanner
